// src/pages/billing/InvoiceListPage.tsx

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useBillingInvoice, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatMoney(money: any): string {
  if (!money) return '--';
  const amount = (money.amount ?? 0) / Math.pow(10, money.decimal_places ?? 2);
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

function statusVariant(status: string): 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
  switch (status) {
    case 'INVOICE_STATUS_PAID': return 'success';
    case 'INVOICE_STATUS_PENDING': case 'INVOICE_STATUS_SENT': return 'primary';
    case 'INVOICE_STATUS_OVERDUE': return 'error';
    case 'INVOICE_STATUS_DRAFT': return 'secondary';
    case 'INVOICE_STATUS_PARTIALLY_PAID': return 'warning';
    case 'INVOICE_STATUS_VOIDED': case 'INVOICE_STATUS_UNCOLLECTIBLE': return 'error';
    default: return 'secondary';
  }
}

function statusLabel(status: string): string {
  return (status || '').replace('INVOICE_STATUS_', '').replace(/_/g, ' ');
}

export const InvoiceListPage = observer(() => {
  const store = useBillingInvoice();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');

  useEffect(() => {
    store.ListInvoices({
      pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
      sort: [], date_range: {} as any, filters: {} as any,
    });
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">Invoices</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">Invoices</h1>
        </div>
        <Link to="/billing/invoices/new"><Button>Create Invoice</Button></Link>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} fullWidth={false} className="flex-1" />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={6} message="No invoices found" />
            ) : (
              store.items.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>
                    <Link to={`/billing/invoices/${item.entity?.id || item.id}`} className="text-primary hover:underline">
                      {String(item.number ?? '--')}
                    </Link>
                  </TableCell>
                  <TableCell>{String(item.customer_name ?? '--')}</TableCell>
                  <TableCell><Badge variant={statusVariant(item.status)} size="sm">{statusLabel(item.status)}</Badge></TableCell>
                  <TableCell>{formatMoney(item.amounts?.net)}</TableCell>
                  <TableCell>{formatDate(item.due_date)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/billing/invoices/${item.entity?.id || item.id}`}><Button variant="ghost" size="sm">View</Button></Link>
                      {item.status === 'INVOICE_STATUS_DRAFT' && (
                        <Link to={`/billing/invoices/${item.entity?.id || item.id}/edit`}><Button variant="ghost" size="sm">{t('common.edit')}</Button></Link>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default InvoiceListPage;
